import {
  Chip,
  HStack,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import { Avatar, Separator } from '@chakra-ui/react';
import useUser from 'api/healthCoach/useUser';
import {
  SENTIMENT,
  FEEDBACK_OPTION_SENTIMENT,
  SENTIMENT_CHIP_VARIANT,
  Sentiment,
  UserFeedbackSurvey,
  FeedbackOption,
} from 'models/userFeedback';
import { formatDate } from 'components/UserDashboard/utils';

const aybleLogo = 'https://app.ayblehealth.com/auth0-ayble-logo.png';

type FeedbackBadgeItem = { label: string; sentiment: Sentiment };

type SurveyData = {
  sentiment?: FeedbackOption;
  symptoms_feedback?: FeedbackOption;
  implementation_feedback?: FeedbackOption;
};

const normalizeFeedback = ({
  sentiment,
  symptoms_feedback,
  implementation_feedback,
}: SurveyData): FeedbackBadgeItem[] => {
  return [sentiment, symptoms_feedback, implementation_feedback].flatMap(
    (item) =>
      item
        ? [
            {
              label: item,
              sentiment: FEEDBACK_OPTION_SENTIMENT[item] ?? SENTIMENT.neutral,
            },
          ]
        : []
  );
};

export const BlockFeedbackItem = ({
  feedbackItem,
}: {
  feedbackItem: UserFeedbackSurvey;
}) => {
  const { user } = useUser();

  const { healthie_id, provider, survey, feedback_day } = feedbackItem;
  const isUserSubmitted = !healthie_id;
  const supportSubmittedFeedback = !!healthie_id && !provider;

  const author = supportSubmittedFeedback
    ? 'Ayble Support'
    : isUserSubmitted
    ? `${user?.first_name} ${user?.last_name}`
    : provider?.display_name || provider?.full_name;

  const title = supportSubmittedFeedback
    ? 'Support'
    : isUserSubmitted
    ? 'App Check-in'
    : 'Patient Reported Feedback';

  return (
    <VStack gap={1} mb={1}>
      <HStack gap={3} alignItems={'center'}>
        <Avatar.Root size={'xs'} w={6} h={6} bg={'bluegrey'}>
          <Avatar.Fallback name={author} />
          <Avatar.Image
            src={supportSubmittedFeedback ? aybleLogo : provider?.image_url}
          />
        </Avatar.Root>

        <HStack px={1}>
          <Text fontSize={'sm'} color={'evergreen'} fontWeight={600}>
            {author}
          </Text>
        </HStack>
      </HStack>

      <HStack gap={1}>
        <Separator
          h={'100%'}
          orientation={'vertical'}
          size={'md'}
          mx={'11px'}
        />

        <VStack flexGrow={1}>
          <HStack gap={4}>
            <VStack
              w={'100%'}
              bgColor={healthie_id ? undefined : 'feather'}
              rounded={'lg'}
              borderWidth={1}
            >
              <HStack py={2} px={3} justifyContent={'space-between'}>
                <Text color={'seafoam'} fontSize={'xs'} fontWeight={600}>
                  {title}
                </Text>
                <Text color={'bluegrey'} fontSize={'xs'} fontWeight={600}>
                  {formatDate(feedback_day ?? undefined, 'MMM D')}
                </Text>
              </HStack>
              <Separator />
              <VStack p={3} gap={2}>
                <HStack gap={2}>
                  {normalizeFeedback(survey).map(({ label, sentiment }) => (
                    <Chip key={label} variant={SENTIMENT_CHIP_VARIANT[sentiment]}>
                      {label}
                    </Chip>
                  ))}
                </HStack>
                {!!survey?.notes && (
                  <Text fontSize={'sm'} fontWeight={400} color={'evergreen'}>
                    {survey.notes}
                  </Text>
                )}
              </VStack>
            </VStack>
          </HStack>
        </VStack>
      </HStack>
    </VStack>
  );
};

export default BlockFeedbackItem;
